import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * One-shot admin tool: merge all records from a source user's email into
 * the currently-authenticated user. Two-step flow — preview first, then
 * confirm. Uses the same fetch mechanism as the rest of the app, so the
 * session cookie is always attached regardless of domain setup.
 */
export default function AdminMergeDialog({ open, onOpenChange }) {
  const [fromEmail, setFromEmail] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const reset = () => { setFromEmail(''); setPreview(null); setConfirmed(null); };

  const runPreview = async () => {
    if (!fromEmail.trim()) { toast.error('Enter the source email'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/merge-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ from_email: fromEmail.trim().toLowerCase() }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { detail: text }; }
      if (!res.ok) {
        toast.error(data.detail || `HTTP ${res.status}`);
        return;
      }
      setPreview(data);
    } catch (e) {
      toast.error(String(e));
    } finally {
      setLoading(false);
    }
  };

  const runMigration = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/merge-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ from_email: fromEmail.trim().toLowerCase(), confirm: true }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { detail: text }; }
      if (!res.ok) {
        toast.error(data.detail || `HTTP ${res.status}`);
        return;
      }
      setConfirmed(data);
      toast.success(`Migrated ${data.total_moved} records. Refresh to see everything.`);
    } catch (e) {
      toast.error(String(e));
    } finally {
      setLoading(false);
    }
  };

  const nonZeroCounts = preview
    ? Object.entries(preview.counts).filter(([, n]) => n > 0)
    : [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-white rounded-md border border-[#E6EBE8] max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Chivo', sans-serif" }}>
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Merge another account into this one
          </DialogTitle>
        </DialogHeader>

        {!preview && !confirmed && (
          <div className="space-y-3">
            <p className="text-sm text-[#6B7A73]">
              Enter the email of the account whose estimates, tasks, clients, and other
              records should be moved into your current account. You'll see a preview
              before anything is changed.
            </p>
            <Input
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="e.g. richvass@gmail.com"
              className="bg-white border-[#E6EBE8]"
              autoFocus
              data-testid="merge-from-email"
            />
          </div>
        )}

        {preview && !confirmed && (
          <div className="space-y-3">
            <div className="bg-[#F9F9F8] border border-[#E6EBE8] rounded-md p-3 text-sm">
              <div className="flex justify-between"><span className="text-[#6B7A73]">From</span><span className="font-mono text-[#1A1D1C]">{preview.from.email}</span></div>
              <div className="flex justify-between mt-1"><span className="text-[#6B7A73]">To</span><span className="font-mono text-[#1A1D1C]">{preview.to.email}</span></div>
            </div>
            {nonZeroCounts.length === 0 ? (
              <p className="text-sm text-[#6B7A73] italic">No records found on that account.</p>
            ) : (
              <>
                <p className="text-sm text-[#1A1D1C]">This will migrate:</p>
                <ul className="text-sm space-y-1 max-h-48 overflow-y-auto">
                  {nonZeroCounts.map(([coll, n]) => (
                    <li key={coll} className="flex justify-between px-2 py-1 rounded hover:bg-[#F0F3F1]">
                      <span className="text-[#3A423E]">{coll.replace(/_/g, ' ')}</span>
                      <span className="font-mono text-[#1A1D1C]">{n}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  Total <strong>{preview.total}</strong> records will move. This cannot be easily undone.
                </p>
              </>
            )}
          </div>
        )}

        {confirmed && (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm">
              <p className="font-semibold text-emerald-800 mb-1">Migration complete</p>
              <p className="text-emerald-700">Moved {confirmed.total_moved} records from {confirmed.from.email}. Refresh the page to see everything under your account.</p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {!preview && !confirmed && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E6EBE8]">Cancel</Button>
              <Button
                onClick={runPreview}
                disabled={loading || !fromEmail.trim()}
                className="bg-[#2D6B68] text-white hover:bg-[#235552]"
                data-testid="merge-preview-btn"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
                Preview
              </Button>
            </>
          )}
          {preview && !confirmed && (
            <>
              <Button variant="outline" onClick={reset} className="border-[#E6EBE8]">Back</Button>
              {nonZeroCounts.length > 0 && (
                <Button
                  onClick={runMigration}
                  disabled={loading}
                  className="bg-amber-600 text-white hover:bg-amber-700"
                  data-testid="merge-confirm-btn"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
                  Yes, migrate {preview.total} records
                </Button>
              )}
            </>
          )}
          {confirmed && (
            <Button onClick={() => { reset(); onOpenChange(false); window.location.reload(); }} className="bg-[#2D6B68] text-white hover:bg-[#235552]">
              Refresh page
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
